import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Search } from "lucide-react";
import { Logo } from "./Logo";
import { SiteSearch } from "./SiteSearch";
import { scrollToId } from "@/lib/scroll";
import { useT, useLang, LangSwitch } from "@/i18n";
import { WHITE_THEME } from "@/lib/theme";

export const LINKS = [
  ["Legno lamellare", "/legno-lamellare"],
  ["Pannelli X-LAM", "/pannelli-xlam"],
  ["Lettiere per animali", "/lettiere-animali"],
  ["Biomassa", "/biomassa"],
  ["Perché noi", "#perche-emma"],
  ["Prodotti", "#prodotti"],
  ["La casa in legno", "#casa-in-legno"],
  ["Balneari", "#balneari"],
  ["Progetti", "#progetti"],
  ["Cantieri", "#cantieri"],
  ["Enti pubblici", "/enti-pubblici"],
  ["Chi siamo", "/chi-siamo"],
  ["Guida tecnica", "/guida-tecnica"],
  ["Sostenibilità", "#sostenibilita"],
  ["Certificazioni", "#certificazioni"],
  ["Contatti", "#contatti"],
];

export const useGo = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id) => {
    if (id.startsWith("/")) return navigate(id);
    if (pathname !== "/") return navigate(`/${id}`);
    return scrollToId(id);
  };
};

// Real link (crawlable href, keeps the language) that still scrolls smoothly / routes in-app on click.
export const NavA = ({ to, onGo, children, ...rest }) => {
  const go = useGo();
  const { lang } = useLang();
  const [path, hash = ""] = (to.startsWith("#") ? `/${to}` : to).split("#");
  const href = `${path}${lang === "it" ? "" : `${path.includes("?") ? "&" : "?"}lang=${lang}`}${hash ? `#${hash}` : ""}`;
  const onClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    onGo?.();
    go(to);
  };
  return <a href={href} onClick={onClick} {...rest}>{children}</a>;
};

export const Nav = () => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchStart, setSearchStart] = useState(null);
  useEffect(() => {
    const onOpen = (e) => { setSearchStart({ ...(e?.detail ?? {}) }); setSearching(true); };
    window.addEventListener("open-search", onOpen);
    return () => window.removeEventListener("open-search", onOpen);
  }, []);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-8"
        data-testid="site-header"
      >
        <nav className={`keep-dark mx-auto flex max-w-[1500px] items-center justify-between rounded-full border border-white/10 bg-[#121110]/95 px-4 text-[#F7F5F0] backdrop-blur-xl transition-[padding] duration-500 sm:px-6 ${scrolled ? "py-1.5" : "py-2.5"}`} data-testid="nav-bar">
          <NavA to="#top" className="shrink-0" data-testid="nav-logo" aria-label="emma industrie home">
            <Logo className={`transition-[height] duration-500 ease-out ${scrolled ? "h-11 sm:h-14" : "h-14 sm:h-[76px]"}`} />
          </NavA>
          <ul className="mx-4 hidden items-center gap-4 min-[2200px]:flex">
            {LINKS.map(([label, id]) => (
              <li key={id}>
                <NavA to={id} className="nav-link whitespace-nowrap font-mono text-xs uppercase tracking-[0.12em]" data-testid={`nav-link-${id.slice(1)}`}>
                  {t(`nav.${id.slice(1)}`, label)}
                </NavA>
              </li>
            ))}
          </ul>
          <div className="flex shrink-0 items-center gap-2">
            <LangSwitch className="hidden sm:flex" />
            <span className="hidden sm:block">
              <NavA to="#contatti" className="btn-amber" data-testid="nav-cta-preventivo">
                {t("nav.quote", "Preventivo")} <ArrowUpRight className="h-4 w-4" />
              </NavA>
            </span>
            <button onClick={() => setSearching(true)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 transition-colors duration-300 hover:border-[#D98A52]" data-testid="nav-search-open" aria-label={t("search.title", "Cerca nel sito")}>
              <Search className="h-5 w-5" />
            </button>
            <button onClick={() => setOpen(true)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 min-[2200px]:hidden" data-testid="nav-menu-open" aria-label="Apri menu">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </motion.header>
      <SiteSearch open={searching} onOpenChange={(v) => { setSearching(v); if (!v) setSearchStart(null); }} start={searchStart} />
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[80] flex flex-col bg-[#121110] px-6 pb-10 pt-6 text-[#F7F5F0]"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo tone={WHITE_THEME ? "dark" : "light"} className="h-10 sm:h-[52px]" />
              <button onClick={() => setOpen(false)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15" data-testid="nav-menu-close" aria-label="Chiudi menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="mt-12 space-y-2">
              {LINKS.map(([label, id], i) => (
                <motion.li key={id} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.25 + i * 0.06 }}>
                  <NavA to={id} onGo={() => setOpen(false)} className="font-display text-4xl font-bold tracking-tight sm:text-5xl" data-testid={`mobile-link-${id.slice(1)}`}>
                    {t(`nav.${id.slice(1)}`, label)}
                  </NavA>
                </motion.li>
              ))}
            </ul>
            <LangSwitch className="mt-10 w-fit" />
            <NavA to="#contatti" onGo={() => setOpen(false)} className="btn-amber mt-auto justify-center" data-testid="mobile-cta-preventivo">
              {t("nav.quoteLong", "Richiedi preventivo")} <ArrowUpRight className="h-4 w-4" />
            </NavA>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
